import React, { useEffect, useState, useCallback } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function BrandingSettings() {
  const [school, setSchool] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/school/me");
      setSchool(data);
      setForm({
        school_name: data.school_name || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
        upi_id: data.upi_id || "",
        receipt_footer: data.receipt_footer || "",
        theme_color: data.theme_color || "#1E3A8A",
        logo_url: data.logo_url || "",
        fee_start_rule: data.fee_start_rule || "admission_month",
      });
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/school/me", cleanPayload(form));
      setSchool(data);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  if (!school && !error) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (error) {
    return <div className="text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6" data-testid="branding-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Settings</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">Branding & contact</h1>
        <p className="text-sm text-slate-600 mt-1">Configure how your school appears across receipts and the app.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="lg:col-span-2 p-5 border border-slate-200 bg-white rounded-lg space-y-4" data-testid="branding-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>School name</Label>
              <Input value={form.school_name} onChange={update("school_name")} className="mt-1.5" data-testid="brand-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Logo URL</Label>
              <Input value={form.logo_url} onChange={update("logo_url")} className="mt-1.5" placeholder="https://…/logo.png" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={update("address")} className="mt-1.5" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={update("phone")} className="mt-1.5" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.email} onChange={update("email")} className="mt-1.5" />
            </div>
            <div>
              <Label>UPI ID</Label>
              <Input value={form.upi_id} onChange={update("upi_id")} className="mt-1.5" data-testid="brand-upi" />
            </div>
            <div>
              <Label>Theme color</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="color"
                  value={form.theme_color}
                  onChange={update("theme_color")}
                  className="h-10 w-14 rounded border border-slate-200"
                />
                <Input value={form.theme_color} onChange={update("theme_color")} data-testid="brand-color" />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Receipt footer</Label>
              <Input value={form.receipt_footer} onChange={update("receipt_footer")} className="mt-1.5" data-testid="brand-footer" />
            </div>

            <div className="md:col-span-2 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Fees</div>
              <div className="mt-1 font-semibold text-slate-900">Fee liability rule</div>
              <p className="text-xs text-slate-600 mt-1">
                When a new student is admitted, from which month do they start owing fees?
              </p>

              <div className="mt-3 space-y-2" data-testid="fee-start-rule-group">
                <label
                  className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                    form.fee_start_rule === "admission_month"
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="fee-start-rule-admission"
                >
                  <input
                    type="radio"
                    name="fee_start_rule"
                    value="admission_month"
                    checked={form.fee_start_rule === "admission_month"}
                    onChange={() => setForm((f) => ({ ...f, fee_start_rule: "admission_month" }))}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">From admission month</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      A student admitted in August owes fees from August onward (not April–July).
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                    form.fee_start_rule === "session_start"
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="fee-start-rule-session"
                >
                  <input
                    type="radio"
                    name="fee_start_rule"
                    value="session_start"
                    checked={form.fee_start_rule === "session_start"}
                    onChange={() => setForm((f) => ({ ...f, fee_start_rule: "session_start" }))}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">From session start (April)</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Every student owes fees for the full session, even mid-year admissions.
                    </div>
                  </div>
                </label>
              </div>

              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900" data-testid="fee-start-rule-note">
                <strong>Note:</strong> Changing this rule only affects students added <em>after</em> you save. Existing students keep their current fee-start month, so reports and pending dues never get retro-recalculated. To change an individual student, open their record and edit their <em>Fee start month</em>.
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-slate-100">
            <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="brand-save-btn">
              <FloppyDisk size={14} /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>

        {/* Live receipt preview */}
        <div className="p-5 border border-slate-200 bg-white rounded-lg" data-testid="receipt-preview">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Receipt preview</div>
          <div className="border border-slate-200 rounded p-4 bg-slate-50">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
              <div
                className="h-10 w-10 rounded flex items-center justify-center text-white font-bold font-heading"
                style={{ backgroundColor: form.theme_color || "#1E3A8A" }}
              >
                {(form.school_name || "S").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate font-heading">{form.school_name || "Your School"}</div>
                <div className="text-[11px] text-slate-500 truncate">{form.address || "School address"}</div>
              </div>
            </div>
            <div className="py-3 text-xs text-slate-600 space-y-1">
              <div>Phone: <span className="text-slate-900">{form.phone || "—"}</span></div>
              <div>Email: <span className="text-slate-900">{form.email || "—"}</span></div>
              <div>UPI: <span className="font-mono text-slate-900">{form.upi_id || "—"}</span></div>
            </div>
            <div className="pt-3 border-t border-slate-200 text-center text-xs text-slate-500 italic">
              {form.receipt_footer || "Receipt footer text will appear here."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
